// Root: sign-in gate, providers, routes for the full IA (DESIGN §2). Pages live in src/pages; the IA lists in src/app/nav.ts.
import { useCallback } from "react";
import { BrowserRouter, MemoryRouter, Navigate, Route, Routes } from "react-router-dom";
import { AppShell } from "./app/AppShell";
import { AppProvider } from "./app/context";
import { DecisionsProvider } from "./app/decisions";
import { EngineProvider } from "./app/engine";
import { LoadingRows, ErrorState, ToastProvider } from "./components";
import type { DataSource } from "./data/source";
import { useLoad } from "./lib/hooks";
import { AgentsPage } from "./pages/Agents";
import { EngagementLayout } from "./pages/engagement/Layout";
import { WsActivity } from "./pages/ws/Activity";
import { WsStatements } from "./pages/ws/Statements";
import { ClientActions, EngagementActions, FirmActions } from "./pages/ws/ActionCenter";
import { ClientPage, ClientsPage } from "./pages/ws/Clients";
import { WsClientDocs } from "./pages/ws/ClientDocs";
import { WsFolders } from "./pages/ws/Folders";
import { WsHome } from "./pages/ws/Home";
import { IssuePage, TailoringPage } from "./pages/ws/Issues";
import { QuestionPage, QuestionsPage } from "./pages/ws/Questions";
import { WsOverview } from "./pages/ws/Overview";
import { WsWorkpaper } from "./pages/ws/WorkpaperPage";
import { WsWorkpapers } from "./pages/ws/Workpapers";
import { OldEngagementRedirect, WorkspaceLayout } from "./pages/ws/Workspace";
import { ENGAGEMENT_PAGES } from "./pages/engagement";
import { WorkpaperPage } from "./pages/engagement/Workpaper";
import { FIRM_PAGES } from "./pages/firm";
import { EngagementsPage } from "./pages/Engagements";
import { HomePage } from "./pages/Home";
import { InboxPage } from "./pages/Inbox";
import { NotFound } from "./pages/NotFound";
import { PortalPage, PortalThreadPage } from "./pages/Portal";
import { RequestsPage } from "./pages/Requests";
import { SignInPage } from "./pages/SignIn";

/** The engagement workspace's screens, under /e/:id (its layout element is the caller's: WorkspaceLayout here, the desk's frame in
 * the local desk — desk/src/ui/Mount.tsx, ADR-0043 amendment 2). */
export function workspaceRoutes() {
  return (
    <>
      <Route index element={<WsOverview />} />
      <Route path="folders/*" element={<WsFolders />} />
      <Route path="workpapers" element={<WsWorkpapers />} />
      <Route path="documents" element={<WsClientDocs />} />
      <Route path="questions" element={<QuestionsPage />} />
      <Route path="questions/:thread" element={<QuestionPage />} />
      <Route path="actions/:tab?" element={<EngagementActions />} />
      <Route path="wp/:ref" element={<WsWorkpaper />} />
      <Route path="issues" element={<Navigate to="../actions/history" relative="path" replace />} />
      <Route path="issues/:key" element={<IssuePage />} />
      <Route path="tailoring/:wp" element={<TailoringPage />} />
      <Route path="activity" element={<WsActivity />} />
      <Route path="statements" element={<WsStatements />} />
      <Route path="details" element={<EngagementLayout />}>
        {ENGAGEMENT_PAGES.map(({ path, Page }) => (path ? <Route key={path} path={path} element={<Page />} /> : <Route key="index" index element={<Page />} />))}
        <Route path="wp/:wp" element={<WorkpaperPage />} />
      </Route>
    </>
  );
}

/** The firm's screens for members of the firm (everything but Home, the engagement list and the portal, which each front has its own of). */
export function firmRoutes() {
  return (
    <>
      <Route path="/clients" element={<ClientsPage />} />
      <Route path="/clients/:clientId" element={<ClientPage />} />
      <Route path="/clients/:clientId/actions/:tab?" element={<ClientActions />} />
      <Route path="/actions/:tab?" element={<FirmActions />} />
      <Route path="/queue" element={<HomePage />} />
      <Route path="/inbox" element={<InboxPage />} />
      <Route path="/requests" element={<RequestsPage />} />
      <Route path="/agents" element={<AgentsPage />} />
      {FIRM_PAGES.map(({ path, Page }) => <Route key={path} path={path} element={<Page />} />)}
    </>
  );
}

export function App({ ds, memory, initial }: { ds: DataSource; memory?: boolean; initial?: string }) {
  const who = useLoad(() => ds.member(), [ds]);
  const signOut = useCallback(() => { void ds.signOut().then(who.reload); }, [ds, who.reload]);
  const switchRole = useCallback((role: string) => { void ds.signIn(role).then(who.reload); }, [ds, who.reload]);
  if (who.error) return <div className="p-6"><ErrorState error={who.error} onRetry={who.reload} /></div>;
  if (who.loading && who.data === undefined) return <div className="p-6"><LoadingRows rows={3} label="Signing in" /></div>;
  const me = who.data;
  if (!me) return <SignInPage ds={ds} onSignedIn={who.reload} />;
  const Router = memory ? MemoryRouter : BrowserRouter;
  const home = me.role === "client" ? "/portal" : "/";
  return (
    <ToastProvider>
      <AppProvider key={me.userId} ds={ds} me={me} signOut={signOut} switchRole={ds.mode === "demo" ? switchRole : undefined}>
        <EngineProvider>
          <DecisionsProvider>
            <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }} {...(memory ? { initialEntries: [initial ?? home] } : {})}>
              <AppShell>
                <Routes>
                  {me.role === "client" ? (
                    <>
                      <Route path="/" element={<Navigate to="/portal" replace />} />
                      <Route path="/portal" element={<PortalPage />} />
                      <Route path="/portal/:engagement/:thread" element={<PortalThreadPage />} />
                    </>
                  ) : (
                    <>
                      <Route path="/" element={<WsHome />} />
                      <Route path="/home" element={<Navigate to="/" replace />} />
                      {firmRoutes()}
                      <Route path="/e/:id" element={<WorkspaceLayout />}>{workspaceRoutes()}</Route>
                      <Route path="/engagements" element={<EngagementsPage />} />
                      <Route path="/engagements/:id/*" element={<OldEngagementRedirect />} />
                      <Route path="/portal" element={<Navigate to="/" replace />} />
                    </>
                  )}
                  <Route path="*" element={me.role === "client" ? <Navigate to="/portal" replace /> : <NotFound />} />
                </Routes>
              </AppShell>
            </Router>
          </DecisionsProvider>
        </EngineProvider>
      </AppProvider>
    </ToastProvider>
  );
}
